import {
  type CreateCustomerPdfAwardRequest,
  type CreateCustomerPdfCelebrityRequest,
  type CustomerPdfAward,
  CustomerPdfAwardListSchema,
  CustomerPdfAwardSchema,
  type CustomerPdfCelebrity,
  CustomerPdfCelebrityListSchema,
  CustomerPdfCelebritySchema,
  type CustomerPdfPartnerLogo,
  CustomerPdfPartnerLogoListSchema,
  type CustomerPdfPublicContent,
  CustomerPdfPublicContentSchema,
  type CreateCustomerPdfStatRequest,
  type CustomerPdfStat,
  CustomerPdfStatListSchema,
  CustomerPdfStatSchema,
  type UpdateCustomerPdfAwardRequest,
  type UpdateCustomerPdfCelebrityRequest,
  type UpdateCustomerPdfStatRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Customer PDF static content API - celebrities ("A Legacy of Trust &
 * Excellence"), awards and the partner logo wall ("Awards & Partners")
 * shown on the customer share page.
 */

// ---------------------------- Celebrities ----------------------------

export const listCelebrities = async (): Promise<CustomerPdfCelebrity[]> => {
  const { data } = await apiClient.get<unknown>('/admin/customer-pdf/celebrities');
  return CustomerPdfCelebrityListSchema.parse(data);
};

export const createCelebrity = async (
  payload: CreateCustomerPdfCelebrityRequest,
): Promise<CustomerPdfCelebrity> => {
  const { data } = await apiClient.post<unknown>('/admin/customer-pdf/celebrities', payload);
  return CustomerPdfCelebritySchema.parse(data);
};

export const updateCelebrity = async (
  id: string,
  payload: UpdateCustomerPdfCelebrityRequest,
): Promise<CustomerPdfCelebrity> => {
  const { data } = await apiClient.patch<unknown>(`/admin/customer-pdf/celebrities/${id}`, payload);
  return CustomerPdfCelebritySchema.parse(data);
};

export const deleteCelebrity = async (id: string): Promise<void> => {
  await apiClient.delete(`/admin/customer-pdf/celebrities/${id}`);
};

// ---------------------------- Awards ----------------------------

export const listAwards = async (): Promise<CustomerPdfAward[]> => {
  const { data } = await apiClient.get<unknown>('/admin/customer-pdf/awards');
  return CustomerPdfAwardListSchema.parse(data);
};

export const createAward = async (
  payload: CreateCustomerPdfAwardRequest,
): Promise<CustomerPdfAward> => {
  const { data } = await apiClient.post<unknown>('/admin/customer-pdf/awards', payload);
  return CustomerPdfAwardSchema.parse(data);
};

export const updateAward = async (
  id: string,
  payload: UpdateCustomerPdfAwardRequest,
): Promise<CustomerPdfAward> => {
  const { data } = await apiClient.patch<unknown>(`/admin/customer-pdf/awards/${id}`, payload);
  return CustomerPdfAwardSchema.parse(data);
};

export const deleteAward = async (id: string): Promise<void> => {
  await apiClient.delete(`/admin/customer-pdf/awards/${id}`);
};

// ---------------------------- Partner logos ----------------------------

export const listPartnerLogos = async (): Promise<CustomerPdfPartnerLogo[]> => {
  const { data } = await apiClient.get<unknown>('/admin/customer-pdf/partner-logos');
  return CustomerPdfPartnerLogoListSchema.parse(data);
};

export const setPartnerLogos = async (imageIds: string[]): Promise<CustomerPdfPartnerLogo[]> => {
  const { data } = await apiClient.put<unknown>('/admin/customer-pdf/partner-logos', { imageIds });
  return CustomerPdfPartnerLogoListSchema.parse(data);
};

// ---------------------------- Stats strip ----------------------------

export const listStats = async (): Promise<CustomerPdfStat[]> => {
  const { data } = await apiClient.get<unknown>('/admin/customer-pdf/stats');
  return CustomerPdfStatListSchema.parse(data);
};

export const createStat = async (
  payload: CreateCustomerPdfStatRequest,
): Promise<CustomerPdfStat> => {
  const { data } = await apiClient.post<unknown>('/admin/customer-pdf/stats', payload);
  return CustomerPdfStatSchema.parse(data);
};

export const updateStat = async (
  id: string,
  payload: UpdateCustomerPdfStatRequest,
): Promise<CustomerPdfStat> => {
  const { data } = await apiClient.patch<unknown>(`/admin/customer-pdf/stats/${id}`, payload);
  return CustomerPdfStatSchema.parse(data);
};

export const deleteStat = async (id: string): Promise<void> => {
  await apiClient.delete(`/admin/customer-pdf/stats/${id}`);
};

// ---------------------------- Public content (preview) ----------------------------

/** The exact payload the customer share page renders - used by the Preview tab. */
export const getPublicContent = async (): Promise<CustomerPdfPublicContent> => {
  const { data } = await apiClient.get<unknown>('/customer-pdf/content');
  return CustomerPdfPublicContentSchema.parse(data);
};
